import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, Spinner, Button, Badge } from "../../shared/ui";
import { Truck, Fuel, MapPin, Calendar, Pencil, Download, RefreshCw } from "lucide-react";
import { TravelEntryEditor } from "../timesheets/EntryEditors";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
};

/**
 * Per-machine travel history — shows every trip recorded against this vehicle.
 * Admin/manager/office can edit any trip inline (clock in/out, km, vehicle,
 * notes). Reflects changes from the central PATCH /api/travel/entries/{id}.
 */
export const MachineTravelHistoryTab = ({ machineId, currentUser, machines: machinesProp }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [machines, setMachines] = useState(machinesProp || []);

  const canEdit = currentUser && ["admin", "manager", "office_staff", "supervisor"].includes(currentUser.role);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/machines/${machineId}/travel-history`);
      setData(r.data);
    } catch (e) {
      toast.error("Couldn't load travel history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Pre-fetch machine list for the vehicle dropdown in TravelEntryEditor (only if not provided)
    if (!machinesProp || machinesProp.length === 0) {
      axios.get(`${API}/machines`).then((r) => setMachines(r.data || [])).catch(() => {});
    }
    /* eslint-disable-next-line */
  }, [machineId]);

  const exportCSV = () => {
    if (!data?.trips?.length) return;
    const rows = [
      ["Date", "Driver", "From", "To", "Finished At", "Start KM", "End KM", "Total KM", "Fuel L", "Fuel $", "Hours", "Status", "Notes"],
      ...data.trips.map((t) => [
        fmtDate(t.clock_in),
        t.user_name || "",
        t.starting_point || "",
        t.destination || "",
        t.finishing_point || "",
        t.starting_km ?? "",
        t.ending_km ?? "",
        t.total_km ?? "",
        t.total_fuel_litres ?? "",
        t.total_fuel_cost ?? "",
        t.total_hours ?? "",
        t.status || "",
        (t.notes || "").replace(/"/g, '""'),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "")}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `travel-history_${data?.machine_name || machineId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="py-12 flex justify-center"><Spinner /></div>;

  return (
    <div className="space-y-4" data-testid="machine-travel-history-tab">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="font-bold text-base uppercase tracking-tight flex items-center gap-2">
              <Truck className="w-5 h-5 text-orange-500" /> Travel History — {data?.machine_name}
            </h3>
            <p className="text-xs text-slate-500 mt-1">Every trip this machine has been on</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={fetchData} data-testid="machine-travel-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
            <Button size="sm" variant="secondary" onClick={exportCSV} disabled={!data?.trips?.length} data-testid="machine-travel-csv">
              <Download className="w-3.5 h-3.5 mr-1" /> CSV
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
            <div className="text-[10px] uppercase font-bold text-slate-500">Trips</div>
            <div className="text-2xl font-bold">{data?.total_trips ?? 0}</div>
          </div>
          <div className="p-3 rounded-lg bg-orange-50 border border-orange-200">
            <div className="text-[10px] uppercase font-bold text-orange-700">Total KM</div>
            <div className="text-2xl font-bold text-orange-700">{(data?.total_km ?? 0).toFixed(1)}</div>
          </div>
          <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
            <div className="text-[10px] uppercase font-bold text-blue-700">Fuel (L)</div>
            <div className="text-2xl font-bold text-blue-700">{(data?.total_fuel_litres ?? 0).toFixed(1)}</div>
          </div>
          <div className="p-3 rounded-lg bg-green-50 border border-green-200">
            <div className="text-[10px] uppercase font-bold text-green-700">Fuel Cost</div>
            <div className="text-2xl font-bold text-green-700">${(data?.total_fuel_cost ?? 0).toFixed(2)}</div>
          </div>
        </div>

        {!data?.trips?.length ? (
          <p className="text-center text-slate-400 py-10 italic">No travel recorded for this machine yet.</p>
        ) : (
          <div className="space-y-2">
            {data.trips.map((t) => {
              if (editingId === t.id) {
                return (
                  <TravelEntryEditor
                    key={t.id}
                    entry={t}
                    machines={machines}
                    onSaved={async () => { setEditingId(null); await fetchData(); }}
                    onCancel={() => setEditingId(null)}
                  />
                );
              }
              const isOpen = !t.clock_out;
              return (
                <div
                  key={t.id}
                  className={`p-3 rounded-lg border ${isOpen ? "bg-amber-50 border-amber-300" : "bg-white border-slate-200"}`}
                  data-testid={`travel-trip-${t.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span className="font-semibold text-sm">{fmtDateTime(t.clock_in)}</span>
                        {isOpen && <Badge variant="warning">Active</Badge>}
                        {t.travel_role && <Badge>{t.travel_role}</Badge>}
                      </div>
                      <div className="mt-1 text-sm text-slate-700 flex items-center gap-1 flex-wrap">
                        <MapPin className="w-3.5 h-3.5 text-slate-400" />
                        <span className="font-semibold">{t.starting_point || "—"}</span>
                        <span className="text-slate-400">→</span>
                        <span className="font-semibold">{t.destination || "—"}</span>
                        {t.finishing_point && t.finishing_point !== t.destination && (
                          <span className="text-xs text-slate-500">(finished at {t.finishing_point})</span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
                        <span>{t.user_name || "Unknown driver"}</span>
                        {t.total_km != null && <span><b className="text-orange-600">{t.total_km}km</b> ({t.starting_km ?? "—"} → {t.ending_km ?? "—"})</span>}
                        {t.total_hours != null && <span>{Number(t.total_hours).toFixed(2)}h</span>}
                        {t.total_fuel_litres > 0 && <span className="flex items-center gap-1"><Fuel className="w-3 h-3" />{t.total_fuel_litres}L · ${(t.total_fuel_cost ?? 0).toFixed(2)}</span>}
                        {t.fuel_stops_count > 0 && <span>{t.fuel_stops_count} fuel stop(s)</span>}
                        {t.incidents && <span className="text-red-600">⚠️ {t.incidents.substring(0, 60)}{t.incidents.length > 60 ? "…" : ""}</span>}
                      </div>
                    </div>
                    {canEdit && (
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(t.id)} data-testid={`edit-travel-${t.id}`}>
                        <Pencil className="w-3 h-3 mr-1" /> Edit
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default MachineTravelHistoryTab;
